/**
 * WorkspaceHeaderLayout Component
 * 
 * Main header component for workspace pages that displays:
 * - Navigation buttons (back to dashboard/workspace)
 * - Workspace title and description (inline editable)
 * - Client and matter details
 * - Member avatars and invite functionality
 * - Real-time online status for workspace members
 * 
 * This component integrates with WorkspaceContext to get real-time updates
 * including member online status changes. It provides comprehensive error handling
 * and user feedback through toast notifications.
 * 
 * @module WorkspaceHeaderLayout
 */

// ============= Imports =============

// React imports
import { ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

// Component library imports
import { AvatarGroup } from '@tw/tw-component-library';
import { Button } from '@/ui';

// Context imports
import { UseAuth } from '@/contexts/AuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';

// Component imports
import { ErrorBoundary } from '@/components/common/ErrorBoundary/ErrorBoundary';

// Styled components imports
import {
  CompactInviteButton,
  HeaderContainer,
  StickyHeaderShell,
  CompactHeaderStack,
  CompactHeaderMainRow,
  CenterSection,
  RightSection,
  RightSectionStack,
  ExtraDetails,
  ClientMatterDetails,
  DetailBlock,
  DetailName,
  DetailNumber,
  MemberCount,
  WorkspaceDataContainer,
  TitleRow,
  WorkspaceTypeBadge,
} from '../WorkspaceHeaderLayout.styles';
import { useCompactTheme } from '@/hooks/useCompactTheme';

// Custom hooks imports
import { useWorkspaceHeaderInlineEdit } from './hooks/useWorkspaceHeaderInlineEdit';
import { useWorkspaceHeaderInvite } from './hooks/useWorkspaceHeaderInvite';
import { useWorkspaceHeaderUserSearch } from './hooks/useWorkspaceHeaderUserSearch';


// Component imports
import { WorkspaceHeaderNavigation } from './components/WorkspaceHeaderNavigation';
import { WorkspaceHeaderEditableField } from './components/WorkspaceHeaderEditableField';
import { WorkspaceHeaderInviteModal } from './components/WorkspaceHeaderInviteModal';
import { WorkspaceHeaderCompactBreadcrumb } from './components/WorkspaceHeaderCompactBreadcrumb';
import { WorkspaceHeaderCompactContext } from './components/WorkspaceHeaderCompactContext';
import { WorkspaceHeaderCompactMeta } from './components/WorkspaceHeaderCompactMeta';
import { WorkspaceHeaderCompactQuickActions } from './components/WorkspaceHeaderCompactQuickActions';
import {
  buildWorkspaceBreadcrumbs,
  WORKSPACE_PAGE_CONTEXT,
} from './workspaceHeaderNavigationConfig';

// Type imports
import { TransformedUserWorkspace } from '@/utils/data/transformWorkSpaceItems';
import { WorkspaceActivePage } from './components/WorkspaceHeaderNavigation';

// ============= Types =============

/**
 * Props for WorkspaceHeaderLayout component
 */
interface WorkspaceHeaderLayoutProps {
  /** Workspace data (may be stale, will be replaced by context data if available) */
  workspaceData: TransformedUserWorkspace;
  /** Current active page for navigation */
  activePage: WorkspaceActivePage;
  /** Optional ask session title to display when in ask-session view */
  askSessionTitle?: string;
}

/**
 * Props for WorkspaceErrorBoundary component
 */
interface WorkspaceErrorBoundaryProps {
  /** Children to wrap with error boundary */
  children: ReactNode;
  /** Optional workspace ID for error context */
  workspaceId?: string;
}

/**
 * Props for WorkspaceHeaderWithErrorBoundary component
 */
interface WorkspaceHeaderWithErrorBoundaryProps {
  /** Workspace data */
  workspaceData: TransformedUserWorkspace;
  /** Current active page */
  activePage: WorkspaceActivePage;
  /** Optional ask session title when viewing a session */
  askSessionTitle?: string;
  /** Children to wrap with error boundary */
  children: ReactNode;
}

// ============= Component =============

/**
 * Main workspace header layout component
 * 
 * This component serves as the header for all workspace pages. It:
 * - Fetches latest workspace data from context (including real-time online status)
 * - Provides inline editing for title and description
 * - Manages member invitations and removal
 * - Displays member avatars with online status indicators
 * - Handles navigation between workspace pages
 * 
 * @param props - Component props
 * @returns Workspace header layout component
 * 
 * @example
 * ```tsx
 * <WorkspaceHeaderLayout
 *   workspaceData={workspaceData}
 *   activePage="ask-hub"
 * />
 * ```
 */
const COMPACT_AVATAR_MAX = 4;

export const WorkspaceHeaderLayout: React.FC<WorkspaceHeaderLayoutProps> = ({
  workspaceData: workspaceDataProp,
  activePage,
  askSessionTitle,
}) => {
  // ============= Context & Hooks =============
  const navigate = useNavigate();
  const { getTransformedWorkspaceById, refreshWorkspaces } = useWorkspace();
  const { getAccessToken, currentUser } = UseAuth();

  // ============= Workspace Data =============

  /**
   * Get workspace data from context (includes real-time online status updates)
   * 
   * This will re-render when the context updates, including when member
   * online status changes. Falls back to prop data if context data unavailable.
   */
  const workspaceDataFromContext = workspaceDataProp?.id
    ? getTransformedWorkspaceById(workspaceDataProp.id)
    : null;

  /**
   * Use context data if available (has latest online status), otherwise fall back to prop
   */
  const workspaceData = workspaceDataFromContext || workspaceDataProp;
  const { isCompactTheme } = useCompactTheme();

  // ============= Custom Hooks =============

  /**
   * Inline editing hook for title and description
   * 
   * Manages edit state, validation, API calls, and error handling
   * for both title and description fields.
   */
  const {
    isEditingTitle,
    editedTitle,
    setEditedTitle,
    titleInputRef,
    handleStartEditTitle,
    handleCancelEditTitle,
    handleSaveTitle,
    isEditingDescription,
    editedDescription,
    setEditedDescription,
    descriptionInputRef,
    handleStartEditDescription,
    handleCancelEditDescription,
    handleSaveDescription,
    isSaving,
  } = useWorkspaceHeaderInlineEdit({
    workspaceId: workspaceData.id,
    title: workspaceData.title,
    description: workspaceData.description,
    getAccessToken,
    refreshWorkspaces,
  });

  /**
   * Invite modal hook
   * 
   * Manages pending members list, adding members, removing users,
   * and admin toggling functionality.
   */
  const {
    pendingMembers,
    isAddingMembers,
    isModalOpen,
    setIsModalOpen,
    addPendingMember,
    removePendingMember,
    togglePendingAdmin,
    addAllMembers,
    deleteUser,
    makeUserOwner,
    resetInviteState,
  } = useWorkspaceHeaderInvite({
    workspaceId: workspaceData.id,
    getAccessToken,
    refreshWorkspaces,
    existingUserIds: workspaceData.workspaceUsers.users,
  });

  /**
   * User search hook
   * 
   * Manages user search state, debouncing, and result formatting
   * for the invite modal.
   */
  const {
    memberInput,
    options,
    isOpen: isSearchDropdownOpen,
    setIsOpen: setIsSearchDropdownOpen,
    isLoading: isSearchLoading,
    handleInviteSearch,
    handleInviteSelect,
    resetSearchState,
  } = useWorkspaceHeaderUserSearch({
    getAccessToken,
  });

  // ============= Handlers =============

  /**
   * Handles user selection from search dropdown
   * 
   * Adds the selected user to pending members list if not already added.
   */
  const handleUserSelect = (selectedLabel: string | null) => {
    const selectedUser = handleInviteSelect(selectedLabel);
    if (selectedUser) {
      addPendingMember(selectedUser);
    }
  };

  /**
   * Handles closing the invite modal
   * 
   * Resets all invite-related state when modal is closed.
   */
  const handleCloseInviteModal = () => {
    setIsModalOpen(false);
    resetInviteState();
    resetSearchState();
  };

  // ============= Data Preparation =============

  /**
   * Prepare avatar data with metadata for AvatarGroup component
   * 
   * Extracts user images, admin status, online status, user IDs, and names
   * from workspace user metadata and data.
   */
  const userImages = workspaceData.workspaceUsers.userImages;
  const userMetadata = workspaceData.workspaceUsers.userMetadata || {};
  const userData = workspaceData.workspaceUsers.userData;

  // Extract arrays for AvatarGroup props
  const avatarImages = Object.values(userMetadata).map((metadata) => metadata.image);
  const avatarIsAdmin = Object.values(userMetadata).map((metadata) => metadata.isOwner);
  const avatarIsOnline = Object.values(userMetadata).map((metadata) => metadata.isOnline);
  const avatarUserIds = Object.values(userMetadata).map((metadata) => metadata.userId);
  const avatarUserNames = Object.values(userData).map(
    (data) => `${data.firstName} ${data.lastName}`
  );

  /** Only workspace owners can make another user owner; pass onMakeOwner only when current user is owner */
  const currentUserIsOwner = Boolean(
    currentUser?.userId && userMetadata[currentUser.userId]?.isOwner
  );

  const memberCount = Object.keys(workspaceData.workspaceUsers.users).length;
  const hasClient =
    workspaceData.clientName ||
    workspaceData.clientCode ||
    workspaceData.clientId;
  const hasMatter =
    workspaceData.matterName ||
    workspaceData.matterCode ||
    workspaceData.matterNumber;

  const pageContext = WORKSPACE_PAGE_CONTEXT[activePage];
  const breadcrumbSegments = useMemo(
    () =>
      buildWorkspaceBreadcrumbs({
        workspaceTitle: workspaceData.title,
        workspaceId: workspaceData.id,
        activePage,
        sessionTitle: askSessionTitle,
      }),
    [workspaceData.title, workspaceData.id, activePage, askSessionTitle],
  );

  const compactClientLine = hasClient
    ? `Client: ${workspaceData.clientName || '—'}`
    : null;
  const compactMatterLine = hasMatter
    ? `Matter: ${workspaceData.matterName || '—'}`
    : null;

  const visibleAvatarCount = isCompactTheme
    ? Math.min(avatarImages.length, COMPACT_AVATAR_MAX)
    : avatarImages.length;
  const compactAvatarImages =
    avatarImages.length > 0
      ? avatarImages.slice(0, visibleAvatarCount)
      : Object.values(userImages).slice(0, visibleAvatarCount);
  const compactAvatarIsAdmin = avatarIsAdmin.slice(0, visibleAvatarCount);
  const compactAvatarIsOnline = avatarIsOnline.slice(0, visibleAvatarCount);
  const compactAvatarUserIds = avatarUserIds.slice(0, visibleAvatarCount);
  const compactAvatarUserNames = avatarUserNames.slice(0, visibleAvatarCount);

  const workspaceQuickActions = (
    <WorkspaceHeaderCompactQuickActions
      onAsk={() =>
        navigate(`/workspace/${workspaceData.id}/ask-session`)
      }
      onUpload={() =>
        navigate(`/workspace/${workspaceData.id}/documents?upload=1`)
      }
      onRunTask={() =>
        navigate(`/workspace/${workspaceData.id}/prompt-library`)
      }
    />
  );

  const memberAvatars = (
    <AvatarGroup
      size={isCompactTheme ? 32 : 48}
      avatars={
        isCompactTheme
          ? compactAvatarImages
          : avatarImages.length > 0
            ? avatarImages
            : Object.values(userImages)
      }
      {...(avatarImages.length > 0 && {
        isAdmin: isCompactTheme ? compactAvatarIsAdmin : avatarIsAdmin,
        isOnline: isCompactTheme ? compactAvatarIsOnline : avatarIsOnline,
        userIds: isCompactTheme ? compactAvatarUserIds : avatarUserIds,
        onDeleteUser: deleteUser,
        onMakeOwner: currentUserIsOwner ? makeUserOwner : undefined,
        userNames: isCompactTheme ? compactAvatarUserNames : avatarUserNames,
      })}
    />
  );

  const inviteControl = isCompactTheme ? (
    <CompactInviteButton type="button" onClick={() => setIsModalOpen(true)}>
      Invite
    </CompactInviteButton>
  ) : (
    <Button variant="secondary" onClick={() => setIsModalOpen(true)}>
      Invite
    </Button>
  );

  const inviteModal = (
    <WorkspaceHeaderInviteModal
      isOpen={isModalOpen}
      onClose={handleCloseInviteModal}
      pendingMembers={pendingMembers}
      memberInput={memberInput}
      options={options}
      isLoading={isSearchLoading}
      isAddingMembers={isAddingMembers}
      isOpenDropdown={isSearchDropdownOpen}
      setIsOpenDropdown={setIsSearchDropdownOpen}
      onSearch={handleInviteSearch}
      onSelect={handleUserSelect}
      onRemoveMember={removePendingMember}
      onToggleAdmin={togglePendingAdmin}
      onAddAllMembers={addAllMembers}
    />
  );

  if (isCompactTheme) {
    return (
      <>
        <StickyHeaderShell $isCompact>
          <HeaderContainer $isCompact>
            <CompactHeaderStack>
              <WorkspaceHeaderCompactBreadcrumb segments={breadcrumbSegments} />

              {pageContext && (
                <WorkspaceHeaderCompactContext
                  eyebrow={pageContext.eyebrow}
                  sessionTitle={
                    pageContext.showSessionTitle ? askSessionTitle : undefined
                  }
                />
              )}

              <CompactHeaderMainRow>
                <WorkspaceHeaderNavigation
                  activePage={activePage}
                  workspaceId={workspaceData.id}
                  isCompactTheme
                />

                <CenterSection $isCompact>
                  <WorkspaceDataContainer $isCompact>
                    <TitleRow>
                      <WorkspaceHeaderEditableField
                        type="title"
                        value={editedTitle}
                        isEditing={isEditingTitle}
                        isSaving={isSaving}
                        isCompactTheme
                        inputRef={titleInputRef}
                        onChange={setEditedTitle}
                        onStartEdit={handleStartEditTitle}
                        onSave={handleSaveTitle}
                        onCancel={handleCancelEditTitle}
                      />
                      {workspaceData.workspaceType && (
                        <WorkspaceTypeBadge>
                          {workspaceData.workspaceType}
                        </WorkspaceTypeBadge>
                      )}
                    </TitleRow>

                    <WorkspaceHeaderEditableField
                      type="description"
                      value={editedDescription}
                      isEditing={isEditingDescription}
                      isSaving={isSaving}
                      isCompactTheme
                      inputRef={descriptionInputRef}
                      onChange={setEditedDescription}
                      onStartEdit={handleStartEditDescription}
                      onSave={handleSaveDescription}
                      onCancel={handleCancelEditDescription}
                      placeholder="No description"
                    />
                  </WorkspaceDataContainer>
                </CenterSection>

                <RightSection $isCompact>
                  <RightSectionStack>
                    {workspaceQuickActions}
                    {workspaceData.workspaceType !== 'Private' && (
                      <>
                        {memberAvatars}
                        {inviteControl}
                      </>
                    )}
                  </RightSectionStack>
                </RightSection>
              </CompactHeaderMainRow>

              <WorkspaceHeaderCompactMeta
                clientLine={compactClientLine}
                matterLine={compactMatterLine}
                memberCount={
                  workspaceData.workspaceType !== 'Private'
                    ? memberCount
                    : undefined
                }
              />
            </CompactHeaderStack>
          </HeaderContainer>
        </StickyHeaderShell>
        {inviteModal}
      </>
    );
  }

  // ============= Render (default theme) =============
  return (
    <>
      <HeaderContainer>
        <WorkspaceHeaderNavigation
          activePage={activePage}
          workspaceId={workspaceData.id}
        />

        <CenterSection>
          <WorkspaceDataContainer>
            <WorkspaceHeaderEditableField
              type="title"
              value={editedTitle}
              isEditing={isEditingTitle}
              isSaving={isSaving}
              inputRef={titleInputRef}
              onChange={setEditedTitle}
              onStartEdit={handleStartEditTitle}
              onSave={handleSaveTitle}
              onCancel={handleCancelEditTitle}
            />

            <WorkspaceHeaderEditableField
              type="description"
              value={editedDescription}
              isEditing={isEditingDescription}
              isSaving={isSaving}
              inputRef={descriptionInputRef}
              onChange={setEditedDescription}
              onStartEdit={handleStartEditDescription}
              onSave={handleSaveDescription}
              onCancel={handleCancelEditDescription}
              placeholder="No description"
            />

            {(hasClient || hasMatter || workspaceData.workspaceType !== 'Private') && (
              <ExtraDetails>
                {(hasClient || hasMatter) && (
                  <ClientMatterDetails>
                    {hasClient && (
                      <DetailBlock>
                        <DetailName>
                          Client Name: {workspaceData.clientName || '—'}
                        </DetailName>
                        {(workspaceData.clientCode ?? workspaceData.clientId) && (
                          <DetailNumber>
                            Client ID:{' '}
                            {workspaceData.clientCode ?? workspaceData.clientId}
                          </DetailNumber>
                        )}
                      </DetailBlock>
                    )}
                    {hasMatter && (
                      <DetailBlock>
                        <DetailName>
                          Matter Name: {workspaceData.matterName || '—'}
                        </DetailName>
                        {(workspaceData.matterCode ??
                          workspaceData.matterNumber) && (
                          <DetailNumber>
                            Matter ID:{' '}
                            {workspaceData.matterCode ??
                              workspaceData.matterNumber}
                          </DetailNumber>
                        )}
                      </DetailBlock>
                    )}
                  </ClientMatterDetails>
                )}
                {workspaceData.workspaceType !== 'Private' && (
                  <MemberCount>
                    {memberCount} member{memberCount !== 1 ? 's' : ''}
                  </MemberCount>
                )}
              </ExtraDetails>
            )}
          </WorkspaceDataContainer>
        </CenterSection>

        <RightSection>
          {workspaceData.workspaceType !== 'Private' && (
            <>
              {memberAvatars}
              {inviteControl}
            </>
          )}
        </RightSection>
      </HeaderContainer>
      {inviteModal}
    </>
  );
};

// ============= Exports =============

/**
 * Workspace Error Boundary Wrapper Component
 * 
 * This component wraps content below the WorkspaceHeaderLayout with an ErrorBoundary
 * to catch and handle errors gracefully without breaking the entire workspace view.
 * 
 * @param props - Component props
 * @returns Error boundary wrapper
 */
export const WorkspaceErrorBoundary: React.FC<WorkspaceErrorBoundaryProps> = ({
  children,
  workspaceId,
}) => {
  return <ErrorBoundary workspaceId={workspaceId}>{children}</ErrorBoundary>;
};

/**
 * Workspace Header with Error Boundary Wrapper
 * 
 * This component includes both the header and an error boundary for the content below.
 * It provides a convenient way to use the header with error handling in one component.
 * 
 * @param props - Component props
 * @returns Workspace header with error boundary
 * 
 * @example
 * ```tsx
 * <WorkspaceHeaderWithErrorBoundary
 *   workspaceData={workspaceData}
 *   activePage="ask-hub"
 * >
 *   <WorkspaceContent />
 * </WorkspaceHeaderWithErrorBoundary>
 * ```
 */
export const WorkspaceHeaderWithErrorBoundary: React.FC<WorkspaceHeaderWithErrorBoundaryProps> = ({
  workspaceData,
  activePage,
  askSessionTitle,
  children,
}) => {
  return (
    <>
      <WorkspaceHeaderLayout
        workspaceData={workspaceData}
        activePage={activePage}
        askSessionTitle={askSessionTitle}
      />
      <WorkspaceErrorBoundary workspaceId={workspaceData.id}>
        {children}
      </WorkspaceErrorBoundary>
    </>
  );
};
